import React, { useEffect, useState, useRef } from 'react';
import { getCurrentUser, logout, getBookings } from '../services/pocketbase';
import pb from '../services/pocketbase'; // Import de l'instance pour les updates
import { useNavigate } from 'react-router-dom';
import { FiUser, FiCalendar, FiLogOut, FiMail, FiPhone, FiClock, FiEdit2, FiCamera, FiSave, FiX } from 'react-icons/fi';
import './UserProfile.css';

function UserProfile() {
    const navigate = useNavigate();
    // On utilise un state initial à null et on le remplit dans useEffect pour éviter les soucis d'hydratation/rendu initial
    const [user, setUser] = useState(getCurrentUser());
    const [activeTab, setActiveTab] = useState('bookings'); // bookings, settings

    // États pour les données
    const [bookings, setBookings] = useState({ upcoming: [], past: [] });
    const [loading, setLoading] = useState(true);
    const [bookingsLoading, setBookingsLoading] = useState(true);

    // États pour l'édition de profil
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        email: '',
        avatar: null
    });
    const [avatarPreview, setAvatarPreview] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        // Vérification de sécurité au montage
        const currentUser = getCurrentUser();

        if (!currentUser) {
            console.log("UserProfile: Pas d'utilisateur trouvé, redirection...");
            // Petit délai pour éviter le flash si c'est juste un délai de chargement du store
            const timer = setTimeout(() => {
                navigate('/');
            }, 100);
            return () => clearTimeout(timer);
        }

        setUser(currentUser);
        setLoading(false);

        // Initialiser le formulaire
        setEditForm({
            name: currentUser.name || '',
            email: currentUser.email || '',
            avatar: null
        });

        fetchMyBookings(currentUser);
    }, [navigate]);

    const fetchMyBookings = async (currentUser) => {
        setBookingsLoading(true);
        try {
            if (!currentUser?.email) return;

            // Utilisation de client_email au lieu de email
            const allBookings = await getBookings({
                filter: `client_email = "${currentUser.email}"`,
                sort: '-date',
                expand: 'service'
            });

            const now = new Date();
            const upcoming = [];
            const past = [];

            allBookings.items.forEach(booking => {
                // Parsing date sécurisé (booking.date est ISO)
                try {
                    const bookingDate = new Date(booking.date);
                    if (isNaN(bookingDate.getTime())) throw new Error("Date invalide");

                    if (bookingDate >= now && booking.status !== 'cancelled' && booking.status !== 'completed') {
                        upcoming.push(booking);
                    } else {
                        past.push(booking);
                    }
                } catch (e) {
                    // Si date invalide, on met dans passé par défaut
                    past.push(booking);
                }
            });

            // Trier les "à venir" du plus proche au plus lointain
            upcoming.sort((a, b) => new Date(a.date) - new Date(b.date));

            setBookings({ upcoming, past });
        } catch (error) {
            console.error("Erreur chargement réservations", error);
        } finally {
            setBookingsLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        window.location.reload();
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditForm(prev => ({ ...prev, avatar: file }));
            setAvatarPreview(URL.createObjectURL(file));
        }
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append('name', editForm.name);
            if (editForm.avatar) {
                formData.append('avatar', editForm.avatar);
            }

            const updatedUser = await pb.collection('users').update(user.id, formData);
            setUser(updatedUser);
            setIsEditing(false);
            // Rafraîchir l'UI si nécessaire
            window.location.reload();
        } catch (error) {
            console.error("Erreur mise à jour profil", error);
            alert("Erreur lors de la mise à jour.");
        }
    };

    const getAvatarUrl = (userRecord) => {
        if (avatarPreview) return avatarPreview;
        if (userRecord?.avatar) {
            return `${pb.baseUrl}/api/files/users/${userRecord.id}/${userRecord.avatar}`;
        }
        return null;
    };

    // Affichage de chargement si user n'est pas encore prêt
    if (loading || !user) {
        return (
            <div className="profile-page">
                <div className="profile-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', flexDirection: 'column' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div className="auth-spinner" style={{ width: '40px', height: '40px', borderColor: '#667eea', borderTopColor: 'transparent', margin: '0 auto 1rem' }}></div>
                        <h2 style={{ color: '#2c3e50' }}>Chargement de votre espace...</h2>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-page">
            <div className="profile-container">

                {/* Sidebar Info Utilisateur */}
                <aside className="profile-sidebar">
                    <div className="profile-card user-info-card">
                        <div className="profile-avatar-container">
                            <div className="profile-avatar">
                                {getAvatarUrl(user) ? (
                                    <img src={getAvatarUrl(user)} alt={user.name} />
                                ) : (
                                    <div className="avatar-placeholder">
                                        {user.name ? user.name.charAt(0).toUpperCase() : <FiUser />}
                                    </div>
                                )}
                            </div>
                            {/* Petit bouton caméra si en mode édition */}
                            {isEditing && (
                                <button className="avatar-upload-btn" onClick={() => fileInputRef.current.click()}>
                                    <FiCamera />
                                </button>
                            )}
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleAvatarChange}
                                style={{ display: 'none' }}
                                accept="image/*"
                            />
                        </div>

                        {!isEditing ? (
                            <>
                                <h2>{user.name || 'Utilisateur'}</h2>
                                <p className="user-role-badge">{user.role === 'admin' ? 'Administrateur' : 'Membre VIP'}</p>
                                <button className="edit-profile-link" onClick={() => { setIsEditing(true); setActiveTab('settings'); }}>
                                    <FiEdit2 /> Modifier mon profil
                                </button>
                            </>
                        ) : (
                            <div className="editing-name-badge">Mode édition</div>
                        )}

                        <div className="sidebar-menu">
                            <button
                                className={`sidebar-menu-item ${activeTab === 'bookings' ? 'active' : ''}`}
                                onClick={() => setActiveTab('bookings')}
                            >
                                <FiCalendar /> Mes Rendez-vous
                            </button>
                            <button
                                className={`sidebar-menu-item ${activeTab === 'settings' ? 'active' : ''}`}
                                onClick={() => setActiveTab('settings')}
                            >
                                <FiUser /> Mes Informations
                            </button>
                        </div>

                        <button onClick={handleLogout} className="logout-btn">
                            <FiLogOut /> Se déconnecter
                        </button>
                    </div>
                </aside>

                {/* Contenu principal */}
                <main className="profile-content">

                    {activeTab === 'bookings' && (
                        <>
                            <h1>✨ Mes Rendez-vous</h1>

                            {/* Rendez-vous à venir */}
                            <div className="bookings-section">
                                <h3>⏳ À venir</h3>
                                {bookingsLoading ? (
                                    <div style={{ textAlign: 'center', padding: '2rem' }}><span className="auth-spinner"></span> Chargement...</div>
                                ) : bookings.upcoming.length > 0 ? (
                                    <div className="bookings-grid">
                                        {bookings.upcoming.map(booking => (
                                            <BookingCard key={booking.id} booking={booking} />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-state-mini">
                                        <p>Aucun rendez-vous prévu prochainement.</p>
                                        <a href="/#reservation" className="new-booking-btn-small">Prendre rendez-vous</a>
                                    </div>
                                )}
                            </div>

                            {/* Historique */}
                            <div className="bookings-section" style={{ marginTop: '3rem' }}>
                                <h3>📚 Historique</h3>
                                {bookings.past.length > 0 ? (
                                    <div className="bookings-grid opacity-low">
                                        {bookings.past.map(booking => (
                                            <BookingCard key={booking.id} booking={booking} />
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted">Aucun historique disponible.</p>
                                )}
                            </div>
                        </>
                    )}

                    {activeTab === 'settings' && (
                        <div className="settings-panel">
                            <h1>👤 Mes Informations</h1>
                            <div className="profile-card settings-card">
                                <form onSubmit={handleUpdateProfile}>
                                    <div className="form-group">
                                        <label>Nom complet</label>
                                        <div className="input-with-icon">
                                            <FiUser />
                                            <input
                                                type="text"
                                                value={editForm.name}
                                                onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                                disabled={!isEditing}
                                                className={isEditing ? 'editable' : ''}
                                                placeholder="Votre nom"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Adresse Email (identifiant)</label>
                                        <div className="input-with-icon">
                                            <FiMail />
                                            <input
                                                type="email"
                                                value={editForm.email}
                                                disabled={true} // Email non modifiable ici pour sécurité
                                                style={{ opacity: 0.7, cursor: 'not-allowed' }}
                                            />
                                        </div>
                                    </div>

                                    <div className="settings-actions">
                                        {!isEditing ? (
                                            <button type="button" className="btn-primary" onClick={() => setIsEditing(true)}>
                                                <FiEdit2 /> Modifier mes informations
                                            </button>
                                        ) : (
                                            <>
                                                <button type="button" className="btn-secondary" onClick={() => { setIsEditing(false); setAvatarPreview(null); }}>
                                                    <FiX /> Annuler
                                                </button>
                                                <button type="submit" className="btn-success">
                                                    <FiSave /> Enregistrer les modifications
                                                </button>
                                                <p className="form-note-small">* Votre avatar sera mis à jour après enregistrement.</p>
                                            </>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                </main>
            </div>
        </div>
    );
}

// Sous-composant pour l'affichage d'une carte de réservation
function BookingCard({ booking }) {
    // Calcul de l'heure
    const timeStr = new Date(booking.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    return (
        <div className={`booking-card status-${booking.status}`}>
            <div className="booking-header">
                <span className="booking-date">
                    {new Date(booking.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' })}
                </span>
                <span className={`status-badge ${booking.status}`}>
                    {booking.status === 'pending' && 'En attente'}
                    {booking.status === 'confirmed' && 'Confirmé'}
                    {booking.status === 'completed' && 'Terminé'}
                    {booking.status === 'cancelled' && 'Annulé'}
                </span>
            </div>
            <div className="booking-body">
                <h4>{booking.expand?.service?.title || 'Service (Titre indisponible)'}</h4>
                <div className="booking-time">
                    <FiClock /> {timeStr}
                </div>
                {booking.notes && (
                    <p className="booking-message">"{booking.notes}"</p>
                )}
            </div>
        </div>
    );
}

export default UserProfile;
